<template>
  <Breadcrumbs :items="breadcrumbs" class="mx-xs md:mx-md my-xs md:my-xs" />

  <Heading
    tag="h1"
    size="lg"
    align="center"
    class="mb-sm md:mb-xl text-charcoal-default"
    data-test-id="branchTitle"
    data-fs="branchTitle"
  >
    Find your nearest MKM branch
  </Heading>

  <section class="h-[700px] relative" :class="[searchNearest ? 'mb-[120px] md:mb-[120px]' : 'mb-unset']" ref="mapEl">
    <BranchMap
      :current-location="currentLocation"
      :search-nearest="searchNearest"
      @select:branch="(branch) => handleSelectBranch(branch)"
    />

    <SimpleCard
      class="absolute top-none left-none right-none md:right-unset md:top-sm md:left-1/2 md:max-w-[650px] bg-white md:-translate-x-1/2"
      rounded
    >
      <Stack tag="div" align="center" gap="2xs" class="mb-2xs">
        <Input
          :model-value="branchSearchInput"
          placeholder="Enter postcode or town"
          label="Search MKM Branches"
          type="search"
          :label-visible="false"
          id="branch-search"
          @update:input-value="(val) => handleInput(String(val))"
          @keydown.enter="handleBranchSearch"
          data-test-id="branchSearchInput"
          data-fs="searchBranchesInput"
        />

        <Button
          variant="secondary"
          size="md"
          @click="handleBranchSearch"
          data-test-id="branchSearchBtn"
          data-fs="branchSearchBtn"
          aria-label="branch-search"
        >
          <Icon name="search" :size="13" />
        </Button>

        <Button
          variant="neutral"
          size="md"
          @click="handleSearchByGeolocation"
          data-test-id="branchLocationBtn"
          data-fs="branchLocationBtn"
          aria-label="branch-geolocation"
        >
          <Icon name="crosshair" :size="13" />
        </Button>
      </Stack>

      <button class="flex justify-center w-full" @click="() => goToBranchList()">
        <Text tag="p" class="text-mkm-blue-light" align="center">See A - Z list of all MKM branches</Text>
      </button>
    </SimpleCard>
  </section>

  <div
    v-if="searchNearest"
    class="absolute top-[950px] md:top-[1050px] w-full"
    ref="nearestEl"
    data-test-id="branchCarousel"
    data-fs="branchCarousel"
  >
    <LazyBranchNearest :lat="currentLocation.lat" :lng="currentLocation.lng" :branch-search="false" hide-title />
  </div>

  <Stack tag="div" justify="center" class="w-full mb-2xl">
    <Button v-if="searchNearest" size="sm" variant="ghost" class="!text-mkm-blue-light" @click="() => handleReset()">
      <template #before>
        <Icon name="search" :size="16" />
      </template>
      Search Again
    </Button>
  </Stack>

  <div ref="branchListEl">
    <BranchList />
  </div>
</template>

<script setup lang="ts">
import type { BreadcrumbItemProps } from "@/components/UI/Breadcrumbs/breadcrumbTypes";
import { Branch } from "mkm-types";

useHead({
  bodyAttrs: {
    class: "bg-neutral-lighter",
  },
});

const UKDefaultCoords = { lat: 54.5, lng: -4 };
const currentLocation = ref(UKDefaultCoords);
const searchNearest = ref<boolean>(false);
const branchSearchInput = ref<string>("");
const isSearching = ref<boolean>(false);

const nearestEl = ref<HTMLElement | null>(null);
const mapEl = ref<HTMLElement | null>(null);
const branchListEl = ref<HTMLElement | null>(null);

const { fetchAllBranches } = useBranches();

await fetchAllBranches();

const breadcrumbs = ref<BreadcrumbItemProps[]>([
  {
    title: "Home",
    disabled: false,
    href: "/",
  },
  {
    title: "Branches",
    disabled: false,
    href: "/branches/",
  },
]);

const handleInput = (value: string) => {
  if (!value) {
    searchNearest.value = false;
  }

  branchSearchInput.value = value;
};

const goToNearestBranches = () => {
  if (process.client) {
    setTimeout(() => {
      if (nearestEl.value) {
        window.scrollTo({
          top: nearestEl.value?.offsetHeight + 300,
          behavior: "smooth",
        });
      }
    }, 500);
  }
};

const handleSearchByGeolocation = async () => {
  if (navigator.geolocation) {
    await nextTick(() => {
      isSearching.value = true;

      navigator.geolocation.getCurrentPosition(async (position) => {
        currentLocation.value = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        isSearching.value = false;
        searchNearest.value = true;
        goToNearestBranches();
      });
    });
  }
};

const handleBranchSearch = async () => {
  if (!branchSearchInput.value) return;

  const geocoder = new google.maps.Geocoder();

  const options = {
    region: "GB",
    address: `${branchSearchInput.value}, UK`,
  };

  await nextTick(async () => {
    await geocoder.geocode(options, async (results: any, status: any) => {
      if (status === "OK") {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();

        currentLocation.value = {
          lat: lat,
          lng: lng,
        };

        searchNearest.value = true;
      }
    });
  });

  goToNearestBranches();
};

const handleSelectBranch = (branch: Branch) => {
  if (!searchNearest.value) {
    searchNearest.value = true;
  }

  currentLocation.value = { lat: Number(branch.coordinates.lat), lng: Number(branch.coordinates.lng) };
};

const handleReset = () => {
  branchSearchInput.value = "";
  currentLocation.value = UKDefaultCoords;

  if (process.client) {
    window.scrollTo({
      top: mapEl.value?.offsetTop,
      behavior: "smooth",
    });
  }

  searchNearest.value = false;
};

const goToBranchList = () => {
  if (process.client) {
    window.scrollTo({
      top: branchListEl.value?.offsetTop,
      behavior: "smooth",
    });
  }
};
</script>
